import type { ISidebarRow, ISidebarRowSubportfolio } from './ISidebarRow';
import type { PinItemType } from './Pinned';

export type PinItemParametersBase = {
  selfKey: string;
  selfCode?: string;
  forceColor?: string;
  type?: PinItemType;

  /**
   * If true, the function will return the changes to make, but not apply them to the colorMap
   */
  justReturnChanges?: boolean;
};

export type PinItemWithChildrenParameters = PinItemParametersBase & {
  /**
   * If this property is supplied, the active sidebar rows supplied to this property will be pinned
   */
  children: ISidebarRow[];
  idsToSkip?: Set<string>;
};

export type PinItemRecursiveParameters = PinItemParametersBase & {
  /**
   * If this property is supplied, all active descendants of the given subportfolio row will be pinned
   */
  subportfolioRowToRecurse: ISidebarRowSubportfolio;
  idsToSkip: Set<string>;
  /**
   * Instead of pinning recursively by going through each subportfolio in order and pinning each of its descendants,
   * it's important to pin the *immediate children* of the row first.
   *
   * This guarantees that the immediate children are colored uniquely,
   * thus allowing the user to more easily distinguish between them).
   */
  children: ISidebarRow[];
};

export type PinItemParameters = PinItemParametersBase | PinItemWithChildrenParameters | PinItemRecursiveParameters;

export type PinItemUnderAnalysisParameters = PinItemParameters &
  (
    | {
        appendChanges?: never;
      }
    | {
        appendChanges: true;

        /**
         * This option is only important for the 'special risk case' where we want to pin a new item under analysis,
         * but not apply the default recursive pinning logic.
         */
        previousItemUnderAnalysisId?: string;
      }
  );

export const isPinItemRecursiveParameter = (params: PinItemParameters): params is PinItemRecursiveParameters => {
  return (
    (params as PinItemRecursiveParameters).subportfolioRowToRecurse !== undefined &&
    (params as PinItemRecursiveParameters).children !== undefined
  );
};

export const isPinItemWithChildrenParameter = (params: PinItemParameters): params is PinItemWithChildrenParameters => {
  return (params as PinItemWithChildrenParameters).children !== undefined;
};
